import { useState, useEffect } from "react";
import { db } from "../firebase/config";
import { doc, getDoc, setDoc } from "firebase/firestore";

// ── Bank Screen (President editable, others read-only) ──────────
// Shows the society's bank account + UPI details, used for
// transferring maintenance dues. Moved out into its own file so it
// can be safely used both from PassbookScreen (inside "My Passbook")
// and anywhere else in the app, without any file needing to import
// from another that already imports it back (a circular import).
export default function BankScreen({ isPresident }) {
  const [form,    setForm]    = useState({});
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState("");

  const BANK_FIELDS = [
    { key: "bankAccount", label: "Bank Account Number" },
    { key: "ifsc",        label: "IFSC Code"           },
    { key: "upi1",        label: "UPI ID 1"            },
    { key: "upi2",        label: "UPI ID 2"            },
  ];

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDoc(doc(db, "settings", "init"));
        if (snap.exists()) {
          const d = snap.data();
          const bankData = {};
          BANK_FIELDS.forEach(f => { if (d[f.key] !== undefined) bankData[f.key] = d[f.key]; });
          setForm(bankData);
        }
      } catch (e) {
        setError("Failed to load bank details.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function handleSave() {
    setSaving(true); setError(""); setSaved(false);
    try {
      await setDoc(doc(db, "settings", "init"), form, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError("Save failed. Check your connection and try again.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return (
    <div style={{ textAlign: "center", padding: "40px", color: "#555570", fontSize: "13px" }}>
      Loading…
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: "16px" }}>
        <div style={{ fontSize: "15px", fontWeight: "700", color: "#e8e8f0" }}>🏦 Bank & UPI Details</div>
        <div style={{ fontSize: "12px", color: "#555570", marginTop: "3px" }}>
          {isPresident ? "Edit and save bank details below." : "Society bank details for transferring dues."}
        </div>
      </div>

      <div style={{
        background: "#42a5f510", border: "1px solid #42a5f530",
        borderRadius: "10px", padding: "10px 14px",
        fontSize: "12px", color: "#42a5f5", marginBottom: "20px",
      }}>
        💡 Use these details to transfer maintenance dues via NEFT or UPI.
      </div>

      {BANK_FIELDS.map(field => (
        <div key={field.key} style={{ marginBottom: "14px" }}>
          <label style={{ display: "block", fontSize: "12px", color: "#8888a8", marginBottom: "5px" }}>
            {field.label}
          </label>
          {isPresident ? (
            <input
              type="text"
              value={form[field.key] ?? ""}
              onChange={e => setForm(prev => ({ ...prev, [field.key]: e.target.value }))}
              style={{
                width: "100%", background: "#0f0f13",
                border: "1px solid #2e2e40", borderRadius: "10px",
                padding: "11px 14px", color: "#e8e8f0",
                fontSize: "14px", outline: "none", boxSizing: "border-box",
              }}
            />
          ) : (
            <div style={{
              width: "100%", background: "#0f0f13",
              border: "1px solid #2e2e40", borderRadius: "10px",
              padding: "11px 14px", color: "#e8e8f0",
              fontSize: "14px", boxSizing: "border-box",
            }}>
              {form[field.key] || <span style={{ color: "#555570" }}>—</span>}
            </div>
          )}
        </div>
      ))}

      {error && (
        <div style={{
          background: "#ff525215", border: "1px solid #ff525240", borderRadius: "10px",
          padding: "10px 14px", fontSize: "13px", color: "#ff5252", marginBottom: "12px",
        }}>⚠ {error}</div>
      )}

      {saved && (
        <div style={{
          background: "#4caf5015", border: "1px solid #4caf5040", borderRadius: "10px",
          padding: "10px 14px", fontSize: "13px", color: "#4caf50", marginBottom: "12px",
        }}>✅ Bank details saved successfully.</div>
      )}

      {isPresident && (
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            width: "100%", background: "#6c63ff", color: "#fff",
            border: "none", borderRadius: "10px", padding: "13px",
            fontSize: "15px", fontWeight: "600",
            cursor: saving ? "not-allowed" : "pointer",
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? "Saving…" : "Save Bank Details"}
        </button>
      )}
    </div>
  );
}
